<template>
  <v-container
    :style="{ maxWidth: screenWidth }">
    <v-card class="mt-2">
      <base-card-title
        label="Terms of delivery without Shipping & Logistics Lead Time coverage"
        icon="mdi-alert-circle-outline"/>
      <v-card-text class="pl-8 pr-8 pb-4">
        <v-row class="pt-3">
          <v-col cols="3" class="pb-0 pt-0">
            <base-input-select-date-picker-thin
              item-label="Active date"
              :selected-value="criteria.activeDate"
              @change="criteria.activeDate = arguments[0]"/>
          </v-col>
          <v-col cols="3" class="pb-0 pt-0">
            <base-input-multi-select-with-flag
              :items="geographicalCountries"
              item-value="id"
              item-text="description"
              item-country="code"
              item-label="Country of delivery"
              :selected-values="criteria.countryOfDeliveryIds"
              @change="criteria.countryOfDeliveryIds = arguments[0]"/>
          </v-col>
          <v-col cols="3" class="pb-0 pt-0">
            <base-input-multi-select-with-flag
              :items="geographicalCountries"
              item-value="id"
              item-text="description"
              item-country="code"
              item-label="Destination country"
              :selected-values="criteria.destinationLogisticsCountryIds"
              @change="criteria.destinationLogisticsCountryIds = arguments[0]"/>
          </v-col>
          <v-col cols="3" class="pb-0 pt-0">
            <multi-select-for-logistics-master-data
              :items="activeModeOfTransports"
              item-value="id"
              item-text="name"
              item-label="Mode of transport"
              logistic-lead-time-to-display="mot"
              :selected-values="criteria.modeOfTransportIds"
              @change="criteria.modeOfTransportIds = arguments[0]"/>
          </v-col>
        </v-row>
        <v-row>
          <v-col>
            <p class="text--secondary mb-0">
              Lists all currently active Terms of Delivery that have no published, active Logistic Lead Time
              satisfying all three matching conditions: country of delivery, mode of transport and destination
              country.
            </p>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <div class="mt-2">
      <base-input-search-and-clear-and-download-buttons
        @search="load"
        @reset="reset"
        @download="downloadExcel"/>
    </div>

    <v-card class="mt-5">
      <v-data-table
        dense
        :headers="tableHeaders"
        :items="items"
        :loading="loading"
        :items-per-page="25"
        :footer-props="{ itemsPerPageOptions: [10, 25, 50, 100] }"
        sort-by="externalId">

        <template v-slot:item.countryOfDelivery="{ item }">
          <base-label-with-flag
            size="small"
            :item-country="item.countryOfDelivery.code"
            :item-label="item.countryOfDelivery.name"/>
        </template>

        <template v-slot:item.destinationLogisticsCountry="{ item }">
          <base-label-with-flag
            size="small"
            :item-country="item.destinationLogisticsCountry.code"
            :item-label="item.destinationLogisticsCountry.name"/>
        </template>

        <template v-slot:item.modeOfTransport.name="{ item }">
          <base-label-with-mode-of-transport :mode-of-transport="item.modeOfTransport"/>
        </template>

        <template v-slot:item.packingMode.name="{ item }">
          <base-label-with-packing-mode :packing-mode="item.packingMode"/>
        </template>

        <template v-slot:item.modeOfTransportCategory.name="{ item }">
          <base-label-with-mode-of-transport-category :mode-of-transport-category="item.modeOfTransportCategory"/>
        </template>

        <template v-slot:item.hazMat="{ item }">
          {{ item.hazMat | formatBoolean }}
        </template>

        <template v-slot:item.actions="{ item }">
          <base-tool-tip
            label="View detail"
            icon="mdi-magnify"
            @click="showViewDialog(item)"/>
        </template>

      </v-data-table>
    </v-card>

    <search-terms-of-delivery-view-details-dialog
      :view-dialog="viewDialog"
      :view-dialog-terms-of-delivery="viewDialogTermsOfDelivery"
      @close="closeViewDialog"/>
  </v-container>
</template>

<script>
import axios from 'axios'
import { mapGetters } from 'vuex'
import BaseCardTitle from '@/base/card/BaseCardTitle'
import BaseLabelWithFlag from '@/base/label/BaseLabelWithFlag'
import BaseLabelWithModeOfTransport from '@/base/label/BaseLabelWithModeOfTransport'
import BaseLabelWithModeOfTransportCategory from '@/base/label/BaseLabelWithModeOfTransportCategory'
import BaseLabelWithPackingMode from '@/base/label/BaseLabelWithPackingMode'
import BaseInputSearchAndClearAndDownloadButtons from '@/base/input/BaseInputSearchAndClearAndDownloadButtons'
import BaseInputSelectDatePickerThin from '@/base/input/BaseInputSelectDatePickerThin'
import BaseInputMultiSelectWithFlag from '@/base/input/BaseInputMultiSelectWithFlag'
import BaseToolTip from '@/base/tooltip/BaseToolTip'
import MultiSelectForLogisticsMasterData from '@/views/termsofdelivery/MultiSelectForLogisticsMasterData'
import SearchTermsOfDeliveryViewDetailsDialog
  from '@/components/termsofdelivery/searchtermsofdelivery/SearchTermsOfDeliveryViewDetailsDialog'
import { downloadBlobMixin } from '@/mixins/downloadBlobMixin'
import { globalMixin } from '@/store/globalMixin'
import { arraysUtils } from '@/mixins/utilsMixin'

export default {
  name: 'TermsOfDeliveryWithoutLltCoverage',
  components: {
    BaseCardTitle,
    BaseLabelWithFlag,
    BaseLabelWithModeOfTransport,
    BaseLabelWithModeOfTransportCategory,
    BaseLabelWithPackingMode,
    BaseInputSearchAndClearAndDownloadButtons,
    BaseInputSelectDatePickerThin,
    BaseInputMultiSelectWithFlag,
    BaseToolTip,
    MultiSelectForLogisticsMasterData,
    SearchTermsOfDeliveryViewDetailsDialog
  },
  mixins: [globalMixin, downloadBlobMixin, arraysUtils],
  data () {
    return {
      loading: false,
      items: [],
      criteria: {
        activeDate: new Date().toISOString().split('T')[0],
        countryOfDeliveryIds: [],
        destinationLogisticsCountryIds: [],
        modeOfTransportIds: []
      },
      viewDialog: false,
      viewDialogTermsOfDelivery: {},
      tableHeaders: [
        { text: 'External ID', align: 'start', sortable: true, value: 'externalId' },
        { text: 'Country of delivery', align: 'start', sortable: true, value: 'countryOfDelivery' },
        { text: 'Destination logistics country', align: 'start', sortable: true, value: 'destinationLogisticsCountry' },
        { text: 'Mode of transport', align: 'start', sortable: true, value: 'modeOfTransport.name' },
        { text: 'MoT category', align: 'start', sortable: false, value: 'modeOfTransportCategory.name' },
        { text: 'Packing mode', align: 'start', sortable: false, value: 'packingMode.name' },
        { text: 'Inco term', align: 'start', sortable: true, value: 'incoTerms.code' },
        { text: 'HazMat', align: 'start', sortable: true, value: 'hazMat' },
        { text: 'Valid from', align: 'start', sortable: true, value: 'validFrom' },
        { text: 'Valid to', align: 'start', sortable: true, value: 'validTo' },
        { text: 'Actions', sortable: false, value: 'actions' }
      ]
    }
  },
  computed: {
    ...mapGetters([
      'geographicalCountries',
      'activeModeOfTransports'
    ])
  },
  async mounted () {
    await Promise.all([
      this.$store.dispatch('getAllGeographicalCountries'),
      this.$store.dispatch('getAllModeOfTransports')
    ])
  },
  methods: {
    load () {
      this.loading = true
      axios.post('/api/touchpoint/terms-of-delivery/without-llt-coverage',
        this.removeEmptyArrays({ ...this.criteria })
      )
        .then(response => {
          this.items = response.data
        })
        .catch(e => console.error('Failed to load TOD without LLT coverage', e))
        .finally(() => {
          this.loading = false
        })
    },
    reset () {
      this.items = []
      this.criteria = {
        activeDate: new Date().toISOString().split('T')[0],
        countryOfDeliveryIds: [],
        destinationLogisticsCountryIds: [],
        modeOfTransportIds: []
      }
    },
    showViewDialog (termsOfDelivery) {
      this.viewDialogTermsOfDelivery = termsOfDelivery
      this.viewDialog = true
    },
    closeViewDialog () {
      this.viewDialog = false
    },
    async downloadExcel () {
      try {
        const response = await axios.post('/api/touchpoint/terms-of-delivery/without-llt-coverage/download.xlsx',
          this.removeEmptyArrays({ ...this.criteria }),
          { responseType: 'blob' }
        )
        this.downloadBlobResponse(response, 'TermsOfDeliveryWithoutLltCoverage.xlsx')
      } catch (e) {
        console.error('Failed to download Terms of Delivery without LLT coverage Excel', e)
      }
    }
  }
}
</script>

<style scoped>
.container {
  max-width: 100%;
}
</style>
